import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import styles from './ChoosePicker.module.css';

interface SkipPromptProps {
  readonly candidateName: string;
  readonly toLabel: string;
  readonly skipped: readonly string[];
  readonly onConfirm: (reason: string) => void;
}

/**
 * F8.16 — an HR Lead skipping a stage says why, and it is logged as an exception
 * (§L23). The confirm stays disabled until there is a reason (#2's rule, for skips).
 */
export function SkipPrompt({ candidateName, toLabel, skipped, onConfirm }: SkipPromptProps): ReactNode {
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const trimmed = reason.trim();
  return (
    <form
      className={styles.picker}
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed !== '') onConfirm(trimmed);
      }}
      onKeyDown={(event) => {
        // 06-qol §2: every form submits on Ctrl/Cmd+Enter.
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && trimmed !== '') {
          event.preventDefault();
          onConfirm(trimmed);
        }
      }}
    >
      <p className={styles.title}>
        Skip {candidateName} to {toLabel}
        <span className={styles.subtitle}>Skips {skipped.join(', ')}. Logged as an exception, with your reason.</span>
      </p>
      <Field label="Why" isRequired>
        {({ inputId }) => <Textarea ref={inputRef} id={inputId} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />}
      </Field>
      <div className={styles.footer}>
        {trimmed === '' && <span className={styles.problem}>Say why this stage is skipped.</span>}
        <Button type="submit" variant="solid" disabled={trimmed === ''}>
          Skip to {toLabel}
        </Button>
      </div>
    </form>
  );
}
